import { useTranslation } from "react-i18next";
import {
  type PreflightPlacementCode,
  type PreflightPlatformCode,
} from "@nexus/contracts";
import { Label, SegmentedControl } from "@nexus/ui";
import { placementsForPlatform } from "../../constants/labels";
import { PlatformPicker } from "../PlatformPicker/PlatformPicker";

const EYEBROW_LABEL =
  "text-muted-foreground text-[11px] font-semibold tracking-wider uppercase";

function RequiredMark() {
  return (
    <span className="text-error font-normal" aria-hidden="true">
      *
    </span>
  );
}

export interface PlatformPlacementSelectProps {
  platform: PreflightPlatformCode | null;
  placement: PreflightPlacementCode | null;
  /** Changing the platform clears the placement (handled by the draft store). */
  onPlatformChange: (platform: PreflightPlatformCode) => void;
  onPlacementChange: (placement: PreflightPlacementCode) => void;
  disabled?: boolean;
}

/**
 * Two dependent segmented toggles: the placement options are driven by the
 * chosen platform via `PLATFORM_PLACEMENTS` (KTD14). Until a platform is picked
 * the placement toggle is replaced by a "select a platform first" hint.
 */
export function PlatformPlacementSelect({
  platform,
  placement,
  onPlatformChange,
  onPlacementChange,
  disabled = false,
}: PlatformPlacementSelectProps) {
  const { t } = useTranslation();
  const placements = platform ? placementsForPlatform(platform) : [];

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {/* min-w-0: grid items default to min-width:auto, which would let the
          platform track blow past its column and overlap placement. */}
      <div className="flex min-w-0 flex-col gap-2">
        <Label className={EYEBROW_LABEL}>
          {t("preflight.platformSelect.platform")} <RequiredMark />
        </Label>
        <PlatformPicker
          fullWidth
          value={platform}
          onChange={(next) => {
            if (next) onPlatformChange(next);
          }}
          disabled={disabled}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-2">
        <Label className={EYEBROW_LABEL}>
          {t("preflight.platformSelect.placement")} <RequiredMark />
        </Label>
        {placements.length > 0 ? (
          <SegmentedControl
            value={placement ?? ""}
            onValueChange={(next) =>
              onPlacementChange(next as PreflightPlacementCode)
            }
            disabled={disabled}
            ariaLabel={t("preflight.platformSelect.placement")}
            options={placements.map((code) => ({
              value: code,
              label: t(`preflight.placement.${code}`),
            }))}
          />
        ) : (
          <p className="text-muted-foreground flex h-8 items-center text-xs">
            {t("preflight.platformSelect.pickPlatformFirst")}
          </p>
        )}
      </div>
    </div>
  );
}